import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Ungültiger Reset-Link. Bitte fordern Sie einen neuen Link an.');
    }
  }, [token]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.new_password || !formData.confirm_password) {
      setError('Bitte füllen Sie alle Felder aus');
      return;
    }

    if (formData.new_password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('Die Passwörter stimmen nicht überein');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        token: token,
        new_password: formData.new_password
      });
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/admin/login');
      }, 3000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Zurücksetzen des Passworts';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl mb-2">Neues Passwort</h1>
          <p className="text-[#666666]">
            {success 
              ? 'Ihr Passwort wurde erfolgreich geändert'
              : 'Erstellen Sie ein neues Passwort für Ihr Konto'
            }
          </p>
        </div>

        {/* Success State */}
        {success ? (
          <div className="bg-white border border-[#E5E5E5] p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h2 className="font-heading text-xl mb-2">Passwort geändert!</h2>
              <p className="text-[#666666] mb-6">
                Ihr Passwort wurde erfolgreich zurückgesetzt. 
                Sie werden in Kürze zum Login weitergeleitet...
              </p>
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 hover:bg-[#1A1A1A] transition-colors"
              >
                Jetzt einloggen
              </Link>
            </div>
          </div>
        ) : (
          <>
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-4 mb-6 flex items-center gap-3">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            {token && (
              <form onSubmit={handleSubmit} className="bg-white border border-[#E5E5E5] p-8">
                <div className="space-y-6">
                  {/* New Password */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Neues Passwort
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        name="new_password"
                        value={formData.new_password}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none pr-12"
                        placeholder="Mindestens 6 Zeichen"
                        data-testid="reset-new-password"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('new')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#0A0A0A]"
                      >
                        {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {formData.new_password && formData.new_password.length < 6 && (
                      <p className="text-red-500 text-xs mt-1">Mindestens 6 Zeichen erforderlich</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Passwort bestätigen
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        name="confirm_password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#E5E5E5] focus:border-[#0A0A0A] focus:outline-none pr-12"
                        placeholder="Passwort wiederholen"
                        data-testid="reset-confirm-password"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#0A0A0A]"
                      >
                        {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {formData.confirm_password && formData.new_password !== formData.confirm_password && (
                      <p className="text-red-500 text-xs mt-1">Passwörter stimmen nicht überein</p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#0A0A0A] text-white py-3 font-medium hover:bg-[#1A1A1A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="reset-submit"
                  >
                    {loading ? (
                      <div className="spinner w-5 h-5 border-white" />
                    ) : (
                      <>
                        <Lock size={18} />
                        Passwort zurücksetzen
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Back to Login */}
            <div className="text-center mt-6">
              <Link
                to="/admin/login"
                className="inline-flex items-center gap-2 text-[#666666] hover:text-[#0A0A0A] transition-colors"
              >
                <ArrowLeft size={18} />
                Zurück zum Login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
